import axios, { AxiosError } from 'axios';
import { t as tCommon, TFunction } from 'i18next';
import { defaults, isFunction, omit } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  MutationFunction,
  useMutation as useMutationReactQuery,
  UseMutationOptions,
  UseMutationResult,
} from 'react-query';
import { toast, Id, ToastOptions } from 'react-toastify';

import { AmountErrorDialogData } from 'components';
import {
  applyGlobalRequestInterceptor,
  applyGlobalResponseInterceptor,
} from 'config/interceptors';
import { SecureConfirmContextType } from 'context';
import { SecureConfirmType } from 'enums';
import { TranslationNamespace } from 'i18n';
import { SecureConfirmData } from 'types';
import { validationUtils } from 'utils';

import { useSecureConfirmContext } from './use-secure-confirm-context.hook';

const notifierTypeKey = 'notifierType';
const notifierMessagesKey = 'notifierMessages';
const mergeNotifierMessagesKey = 'mergeNotifierMessages';
const onBeforeMutationKey = 'onBeforeMutation';
const requireSecureConfirmKey = 'requireSecureConfirm';
const secureConfirmTypesKey = 'secureConfirmTypes';
const onAmountErrorKey = 'onAmountError';
type NotifierType = 'save' | 'remove' | 'execute' | 'none';
const defaultNotifierType: NotifierType = 'save';
type NotifierMessages = {
  loading?: string | null;
  success?: ((data: any) => string | undefined) | string | null;
  error?: ((error: any) => string | undefined) | string | null;
};
type ExtendOptions = {
  [notifierTypeKey]?: NotifierType;
  [notifierMessagesKey]?: NotifierMessages;
  [mergeNotifierMessagesKey]?: NotifierMessages;
  [onBeforeMutationKey]?: (data: any) => any;
  [requireSecureConfirmKey]?: boolean;
  [secureConfirmTypesKey]?: SecureConfirmType[];
  [onAmountErrorKey]?: (error: AmountErrorDialogData) => void;
};
type InternalOptions = {
  onRequestStart: () => void;
};

function getNotifierMessages(
  t: TFunction,
  notifierType: NotifierType = defaultNotifierType,
): NotifierMessages {
  if (notifierType === 'none') {
    return null as any as NotifierMessages;
  }
  return {
    loading: t(`${notifierType}.loading`),
    success: t(`${notifierType}.success`),
    error: t(`${notifierType}.error`),
  };
}

function extendOptions<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  t: TFunction,
  internalOptions: InternalOptions,
  options?: Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    'mutationKey' | 'mutationFn'
  > &
    ExtendOptions,
): Partial<
  Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >
> {
  let toastId: Id;
  const mergedMessages = options?.[mergeNotifierMessagesKey];
  const defaultMessages = getNotifierMessages(t, options?.[notifierTypeKey]);
  const messages: NotifierMessages | null = mergedMessages
    ? defaults({ ...mergedMessages }, defaultMessages)
    : options?.[notifierMessagesKey] || defaultMessages;
  const customErrorMessage = mergedMessages
    ? mergedMessages.error
    : options?.[notifierMessagesKey]?.error;
  return {
    ...(options &&
      omit(
        options,
        notifierTypeKey,
        notifierMessagesKey,
        mergeNotifierMessagesKey,
        requireSecureConfirmKey,
        secureConfirmTypesKey,
        onAmountErrorKey,
      )),
    onMutate: (
      variables: TVariables,
    ): Promise<TContext | undefined> | TContext | undefined => {
      if (options?.[requireSecureConfirmKey]) {
        internalOptions.onRequestStart = () => {
          if (messages?.loading) {
            toastId = toast.loading(messages.loading);
          }
        };
      } else {
        if (messages?.loading) {
          toastId = toast.loading(messages.loading);
        }
      }
      return options?.onMutate?.(variables);
    },
    onSuccess: (
      data: TData,
      variables: TVariables,
      context: TContext | undefined,
    ): Promise<unknown> | void => {
      if (messages?.success) {
        const options: ToastOptions = {
          type: 'success',
          isLoading: false,
        };
        const message = isFunction(messages.success)
          ? messages.success(data)
          : messages.success;
        if (toastId) {
          toast.update(toastId, {
            ...options,
            closeButton: true,
            autoClose: 3000,
            render: message,
          });
        } else {
          toast.success(message, options);
        }
      } else if (toastId) {
        toast.dismiss(toastId);
      }
      return options?.onSuccess?.(data, variables, context);
    },
    onError: (
      error: TError,
      variables: TVariables,
      context: TContext | undefined,
    ): Promise<unknown> | void => {
      if (axios.isCancel(error)) {
        if (toastId) {
          toast.dismiss(toastId);
        }
        return;
      }

      if (messages?.error) {
        const amountError =
          options?.onAmountError &&
          validationUtils.getAmountError(error as any);
        if (amountError) {
          options.onAmountError!(amountError);
        }

        const toastOptions: ToastOptions = {
          type: 'error',
          isLoading: false,
        };
        let message;

        if (!options?.[notifierMessagesKey]) {
          if (validationUtils.isTwoFAError(error as any)) {
            message = tCommon('errors.two_fa');
          } else if (
            validationUtils.isSecurePasswordConfirmError(error as any)
          ) {
            message = tCommon('errors.password');
          } else if (validationUtils.isEntityInUseError(error as any)) {
            message = tCommon('errors.in_use');
          } else if (validationUtils.isTradeMethodNotFoundError(error as any)) {
            message = tCommon('errors.trade_method_not_found');
          }
        }

        if (!message && customErrorMessage != null) {
          message = isFunction(customErrorMessage)
            ? customErrorMessage(error)
            : customErrorMessage;
        }

        if (!message) {
          message = validationUtils.getServerErrorMessage(error as AxiosError);
        }

        if (!message) {
          message = getNotifierMessages(t, options?.[notifierTypeKey]).error;
        }

        if (toastId) {
          toast.update(toastId, {
            ...toastOptions,
            closeButton: true,
            autoClose: 3000,
            render: message,
          });
        } else {
          toast.error(message, toastOptions);
        }
      } else if (toastId) {
        toast.dismiss(toastId);
      }
      return options?.onError?.(error, variables as any as TVariables, context);
    },
  };
}

function getMutationFn<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  mutationFn: MutationFunction<TData, TVariables>,
  options:
    | (Omit<
        UseMutationOptions<TData, TError, TVariables, TContext>,
        'mutationKey' | 'mutationFn'
      > &
        ExtendOptions)
    | undefined,
  internalOptions: InternalOptions,
  secureConfirmContext?: SecureConfirmContextType,
): MutationFunction<TData, TVariables> {
  return async (variables: TVariables) => {
    const formattedVariables = options?.onBeforeMutation
      ? options.onBeforeMutation(variables)
      : variables;

    let axiosInstance = axios.create();
    let secureConfirmData: SecureConfirmData | undefined;

    if (options?.requireSecureConfirm) {
      if (!secureConfirmContext?.requestSecureConfirm) {
        throw new Error('Secure confirm context is missing!');
      }

      try {
        secureConfirmData = await secureConfirmContext.requestSecureConfirm(
          options?.secureConfirmTypes,
        );
      } catch (error) {
        return Promise.reject(new axios.Cancel('Secure confirm canceled'));
      }

      axiosInstance.interceptors.request.use((config) => {
        if (secureConfirmData?.password) {
          config.headers.set('X-Auth-Password', secureConfirmData.password);
        }
        if (secureConfirmData?.code) {
          config.headers.set('X-Auth-Two-FA', secureConfirmData.code);
        }
        return config;
      });

      applyGlobalRequestInterceptor(axiosInstance);
      applyGlobalResponseInterceptor(axiosInstance);
      internalOptions?.onRequestStart?.();
      return mutationFn({ ...formattedVariables, axiosInstance });
    }

    return mutationFn(formattedVariables);
  };
}

export function useMutation<
  TData = unknown,
  TError = AxiosError,
  TVariables = void,
  TContext = unknown,
>(
  mutationFn: MutationFunction<TData, TVariables>,
  options?: Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    'mutationKey' | 'mutationFn'
  > &
    ExtendOptions,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.notifier.http',
  });

  const secureConfirmContext = useSecureConfirmContext(
    !options?.[requireSecureConfirmKey],
  );

  const internalOptions = {} as InternalOptions;

  return useMutationReactQuery(
    getMutationFn(mutationFn, options, internalOptions, secureConfirmContext),
    extendOptions(t, internalOptions, options),
  );
}
